import React, { useMemo } from 'react';
import styles from '../index.less';
import { Space, Tag, Tooltip, Typography } from 'antd';
import { ClockCircleOutlined, FileTextOutlined, MailOutlined, TagsOutlined } from '@ant-design/icons';
import LeadEmail from '@/components/Lead/LeadEmail';
import AgentPhone from '@/components/Lead/AgentPhone';
import { AgentActionTag } from '@/components/ActionTag';
import { F } from '@/utils/intl';
import { useSelector } from 'umi';
import { getAvailableRangesForCalendarDay } from '@/components/Kanban/ServiceRequests/Calendar/utils';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import * as ServiceRequestSelectors from '@/selectors/serviceRequestSelectors';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import { IAgentWithAvailability } from '@/typings/models/Assignee';
import { sortByLabel } from '@/components/SkillsDrawer/utils';

const CalendarHeaderAgentOverviewPopover = ({ agent = {} }: { agent: IAgentWithAvailability }) => {
  const calendarDate = useSelector(ServiceRequestSelectors.selectCalendarDate);
  const timezone = useSelector(PreferencesSelectors.selectSelectedTimezone);
  // The column's branch: its working hours are branch-local, the popover shows
  // them in the calendar's timezone (like the grid shading).
  const branchTimezone: string | undefined = agent.branch?.timezone;
  const availableRanges = useMemo(
    () => getAvailableRangesForCalendarDay(agent.availability, dayjs(calendarDate).tz(timezone), timezone, branchTimezone),
    [agent.availability, calendarDate, timezone, branchTimezone],
  );
  const customSchedules = useMemo(
    () => (agent.customServiceAvailabilities ?? []).map((entry) => ({
      ...entry,
      ranges: getAvailableRangesForCalendarDay(entry.availability, dayjs(calendarDate).tz(timezone), timezone, entry.timezone),
    })),
    [agent.customServiceAvailabilities, calendarDate, timezone],
  );
  const isConverted = !!branchTimezone && branchTimezone !== timezone;
  return <div className={styles.eventLeadOverviewPopoverContent}>
    <AgentActionTag size={'large'} agent={{
      ...agent,
      // A calendar resource's own `id` is a resource-key array
      // ([`<branchServiceId>__<agentId>`, `GLOBAL__<agentId>`]); the real user id
      // lives on assigneeId. Plain agents (booking picker) carry it as `id`.
      // Keep this override last or the drawer/route gets the key.
      id: agent.assigneeId ?? agent.id,
    }} showAgentColor />
    <div>
      <Space>
        <MailOutlined />
        <LeadEmail lead={{ customer: agent }} readonly />
      </Space>
    </div>
    <div>
      <Space>
        <AgentPhone agent={agent} readonly />
      </Space>
    </div>
    {!isEmpty(agent.skills) && (
      <div>
        <Space size={2} align={'start'}>
          <Tooltip title={<F id={'entity.skill.plural'} defaultMessage={'Skills'} />}>
            <TagsOutlined />
          </Tooltip>
          <div>
            {sortByLabel(agent.skills).map((skill) => <Tag key={skill.id}>{skill.label}</Tag>)}
          </div>
        </Space>
      </div>
    )}
    <div>
      <Space size={2} align={'start'}>
        <ClockCircleOutlined />
        <div>
          {agent.hasCustomServiceAvailability && (
            <div><Tag color={'lime'} variant={'filled'} className={styles.customScheduleTag}>
              <F id={'pages.users.custom_schedule'} defaultMessage={'Custom schedule'} />
            </Tag></div>
          )}
          {isEmpty(availableRanges) ? ' - ' : availableRanges?.map(range => <Tag key={range}>{range}</Tag>)}
          {isConverted && (
            <Tooltip title={<F id={'pages.serviceRequest.agentAvailabilityBranchTimezone'}
                               defaultMessage={'Branch time zone: {timezone}'}
                               values={{ timezone: branchTimezone }} />}>
              <div><Typography.Text type={'secondary'}><small>{timezone}</small></Typography.Text></div>
            </Tooltip>
          )}
        </div>
      </Space>
    </div>
    {!isEmpty(customSchedules) && (
      <div className={styles.customSchedules}>
        <div><Tag color={'lime'} variant={'filled'} className={styles.customScheduleTag}>
          <F id={'pages.users.custom_schedule'} defaultMessage={'Custom schedule'} />
        </Tag></div>
        {customSchedules.map((entry) => (
          <div key={entry.branchServiceId}>
            <Typography.Text type={'secondary'}><small>{entry.branchName} · {entry.serviceName}</small></Typography.Text>
            <div>{isEmpty(entry.ranges) ? ' - ' : entry.ranges.map((range) => <Tag key={range}>{range}</Tag>)}</div>
          </div>
        ))}
      </div>
    )}
    {agent.personal_id && <div>
      <Space size={2}>
        <FileTextOutlined />
        <pre style={{margin: 0}}>{agent.personal_id}</pre>
      </Space>
    </div>}
    {/*<div>*/}
    {/*  <Space>*/}
    {/*    <Space>*/}
    {/*      <CalendarOutlined/>*/}
    {/*      <span>Appointments Today</span>*/}
    {/*    </Space>*/}
    {/*    <span>10</span>*/}
    {/*  </Space>*/}
    {/*</div>*/}
    {/*<div>*/}
    {/*  <Space>*/}
    {/*    <Space>*/}
    {/*      <IoLanguageOutline className={'anticon'}/>*/}
    {/*      <span>Language</span>*/}
    {/*    </Space>*/}
    {/*    <span>{languages['en-GB'].icon}</span>*/}
    {/*  </Space>*/}
    {/*</div>*/}

  </div>;
};

export default CalendarHeaderAgentOverviewPopover;
